import React from 'react';
import PropTypes from 'prop-types';
import { Form, Icon, Popconfirm } from 'antd';
import { autobind } from 'core-decorators';
import moment from 'moment';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import DataTables from 'components-desktop/DataTables';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormInput from 'components-desktop/FormInput';
import FormSelect from 'components-desktop/FormSelect';
import ChangeHistoriesButton from 'components-desktop/histories/ChangeHistoriesButton';
import misc from 'misc/misc';

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class PatientAbsences extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      adding: false,
      absences: [],
    };
  }

  componentDidMount() {
    this.id = this.parseQueryParam('id', this.props.location);
    this.getPatientAbsences();
  }

  async getPatientAbsences() {
    const params = { patientId: this.id };
    const absences = await this.callServiceForRemoteObjects('/absence/get_list', params);

    this.setState({
      absences,
    });
  }

  onAddClick() {
    this.setState({
      adding: true,
    });
  }

  onUpdateClick(record) {
    const validatingFields = [
      `type-${record.id}`,
      `startDate-${record.id}`,
      `endDate-${record.id}`,
      `remark-${record.id}`,
    ];
    this.props.form.validateFieldsAndScroll(validatingFields, async (err, values) => {
      if (!err) {
        const params = {
          id: record.id,
          type: values[`type-${record.id}`],
          startDate: misc.toDateString(values[`startDate-${record.id}`]),
          endDate: misc.toDateString(values[`endDate-${record.id}`]),
          remark: values[`remark-${record.id}`],
        };
        await this.callService('/absence/update', params);

        this.notifySuccess('Success', 'The absence record has been updated.');
      }
    });
  }

  async onDeleteClick(record) {
    await this.callService('/absence/delete', { id: record.id });

    this.notifySuccess('Success', 'The absence record has been deleted.');

    this.getPatientAbsences();
  }

  onSaveClick() {
    const validatingFields = [
      'type-0',
      'startDate-0',
      'endDate-0',
      'remark-0',
    ];
    this.props.form.validateFieldsAndScroll(validatingFields, async (err, values) => {
      if (!err) {
        const params = {
          patientId: this.id,
          type: values['type-0'],
          startDate: misc.toDateString(values['startDate-0']),
          endDate: misc.toDateString(values['endDate-0']),
          remark: values['remark-0'],
        };
        await this.callService('/absence/add', params);

        this.notifySuccess('Success', 'A new absence record has been added.');

        this.onCancelClick();
        this.getPatientAbsences();
      }
    });
  }

  onCancelClick() {
    this.setState({
      adding: false,
    });
  }

  createColumns() {
    const { form } = this.props;

    return [{
      title: 'Type',
      dataIndex: 'type',
      sorter: (a, b) => a.type?.localeCompare(b.type),
      render: (text, record) => (
        <FormSelect
          width="match_parent"
          height="wrap_content"
          margin="0px 0px"
          form={form}
          name={`type-${record.id}`}
          placeholder="Select..."
          allowClear={false}
          options={['Hospitalized', 'Vacation', 'Other']}
          initialValue={record.type}
          isRequired
        />
      ),
    }, {
      title: 'Start Date',
      dataIndex: 'startDateUsString',
      sorter: (a, b) => a.startDate - b.startDate,
      defaultSortOrder: 'descend',
      render: (text, record) => (
        <FormDatePicker
          width="match_parent"
          height="wrap_content"
          margin="0px 0px"
          form={form}
          name={`startDate-${record.id}`}
          label=""
          placeholder="mm/dd/yyyy"
          initialValue={record.startDateUsString && moment(record.startDateUsString, 'MM/DD/YYYY')}
          isRequired
        />
      ),
    }, {
      title: 'End Date',
      dataIndex: 'endDateUsString',
      sorter: (a, b) => a.endDate - b.endDate,
      render: (text, record) => (
        <FormDatePicker
          width="match_parent"
          height="wrap_content"
          margin="0px 0px"
          form={form}
          name={`endDate-${record.id}`}
          label=""
          placeholder="mm/dd/yyyy"
          initialValue={record.endDateUsString && moment(record.endDateUsString, 'MM/DD/YYYY')}
          isRequired
        />
      ),
    }, {
      title: 'Remark',
      dataIndex: 'remark',
      sorter: (a, b) => a.remark?.localeCompare(b.remark),
      render: (text, record) => (
        <FormInput
          width="match_parent"
          height="wrap_content"
          margin="0px 0px"
          form={form}
          name={`remark-${record.id}`}
          label=""
          max={2000}
          initialValue={record.remark}
        />
      ),
    }, {
      title: 'Actions',
      render: (text, record) => (
        <FlexRowContainer
          width="match_parent"
          height="wrap_content"
          justifyContent="space-around"
        >
          {record.id !== 0 &&
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              fontSize="14px"
              onClick={() => this.onUpdateClick(record)}
            >
              Update
            </styledComp.A>
          }

          {record.id !== 0 &&
            <Popconfirm
              title="Are you sure you want to delete this record?"
              placement="top"
              okText="Confirm"
              cancelText="Cancel"
              onConfirm={() => this.onDeleteClick(record)}
            >
              <styledComp.A
                width="wrap_content"
                height="wrap_content"
                fontSize="14px"
              >
                Delete
              </styledComp.A>
            </Popconfirm>
          }

          {record.id === 0 &&
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              fontSize="14px"
              onClick={this.onSaveClick}
            >
              Save
            </styledComp.A>
          }

          {record.id === 0 &&
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              fontSize="14px"
              onClick={this.onCancelClick}
            >
              Cancel
            </styledComp.A>
          }
        </FlexRowContainer>
      ),
    }];
  }

  createDataSource(absences) {
    const dataSource = [];
    absences.forEach((absence, index) => {
      dataSource.push({
        key: absence.getId(),
        id: absence.getId(),
        type: absence.getString('type'),
        startDate: absence.getLong('startDate'),
        startDateUsString: absence.getString('startDateUsString'),
        endDate: absence.getLong('endDate'),
        endDateUsString: absence.getString('endDateUsString'),
        remark: absence.getString('remark'),
      });
    });

    // If adding, add a new empty row at the top.
    if (this.state.adding) {
      dataSource.unshift({
        key: 0,
        id: 0,
        type: '',
        startDate: null,
        startDateUsString: null,
        endDate: null,
        endDateUsString: null,
        remark: '',
      });
    }

    return dataSource;
  }

  renderContent() {
    const { ...layoutProps } = this.props;
    const { absences } = this.state;

    return (
      <FlexColContainer
        {...layoutProps}
        padding="20px 20px"
      >
        <FlexRowContainer
          width="match_parent"
          height="wrap_content"
          margin="0 0 20px 0"
          justifyContent="space-between"
          alignItems="center"
        >
          <CustomButton
            width="150px"
            height="34px"
            backgroundColor="green_haze"
            fontSize="14px"
            type="primary"
            onClick={this.onAddClick}
          >
            ADD NEW ITEM
            <Icon type="plus" />
          </CustomButton>

          <ChangeHistoriesButton
            width="wrap_content"
            height="wrap_content"
            title="Change History"
            table="Absence"
            patientId={Number(this.id)}
          />
        </FlexRowContainer>

        <DataTables
          width="match_parent"
          height="wrap_content"
          color="blue_steel"
          fontSize="14px"
          size="small"
          isInsidePanel={false}
          withColumnFilter={false}
          columns={this.createColumns()}
          dataSource={this.createDataSource(absences)}
        />
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
PatientAbsences.propTypes = propTypes;

export default Form.create()(PatientAbsences);
